import React, { useState, useRef, useCallback, useEffect } from 'react';
import SpeedGauge from './SpeedGauge';
import api from '../services/api';

// Detect OS from user agent
function detectOS(ua) {
  if (/Windows NT 10/.test(ua)) return 'Windows 10';
  if (/Windows NT 11/.test(ua)) return 'Windows 11';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X ([0-9_]+)/.test(ua)) {
    const v = ua.match(/Mac OS X ([0-9_]+)/)[1].replace(/_/g, '.');
    return `Macintosh ${v}`;
  }
  if (/Android ([0-9.]+)/.test(ua)) return `Android ${ua.match(/Android ([0-9.]+)/)[1]}`;
  if (/iPhone OS ([0-9_]+)/.test(ua)) return `iOS ${ua.match(/iPhone OS ([0-9_]+)/)[1].replace(/_/g,'.')}`;
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

// Detect browser
function detectBrowser(ua) {
  if (/Edg\/([0-9.]+)/.test(ua))    return `Edge ${ua.match(/Edg\/([0-9.]+)/)[1]}`;
  if (/Chrome\/([0-9.]+)/.test(ua)) return `Chrome ${ua.match(/Chrome\/([0-9.]+)/)[1]}`;
  if (/Firefox\/([0-9.]+)/.test(ua)) return `Firefox ${ua.match(/Firefox\/([0-9.]+)/)[1]}`;
  if (/Safari\/([0-9.]+)/.test(ua) && /Version\/([0-9.]+)/.test(ua))
    return `Safari ${ua.match(/Version\/([0-9.]+)/)[1]}`;
  return 'Unknown';
}

function collectClientInfo() {
  const ua = navigator.userAgent;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return {
    os: detectOS(ua),
    browser: detectBrowser(ua),
    userAgent: ua,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screenResolution: { width: window.screen.width, height: window.screen.height },
    colorDepth: window.screen.colorDepth,
    devicePixelRatio: window.devicePixelRatio || 1,
    language: navigator.language || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack === '1',
    connectionType: conn?.effectiveType || conn?.type || 'unknown'
  };
}

const STEP_FORM = 'form';
const STEP_TEST = 'test';
const STEP_RESULTS = 'results';
const STEP_SUCCESS = 'success';

const PHASES = ['Connecting...', 'Testing Ping...', 'Testing Download...', 'Testing Upload...', 'Finishing...'];

export default function SpeedTestPage() {
  const [step, setStep] = useState(STEP_FORM);
  const [form, setForm] = useState({ name: '', email: '', phone: '', description: '', features: '' });
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState(0);
  const [liveValues, setLiveValues] = useState({ download: 0, upload: 0, ping: 0, jitter: 0 });
  const [results, setResults] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [images, setImages] = useState([]);
  const speedtestRef = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.description.trim()) e.description = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleFormChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: undefined }));
  }

  async function startTest() {
    if (!validate()) return;
    setStep(STEP_TEST);
    setPhase(0);
    setLiveValues({ download: 0, upload: 0, ping: 0, jitter: 0 });

    // Collect client info
    const info = collectClientInfo();

    // Fetch IP/ISP
    try {
      const { data } = await api.get('/speedtest/getIP');
      info.ip = data.ip || '';
      info.isp = data.rawIsp || '';
    } catch {
      info.ip = '';
      info.isp = '';
    }
    setClientInfo(info);

    // Wait for LibreSpeed to be available (script may still be loading)
    let attempts = 0;
    while (!window.Speedtest && attempts < 30) {
      await new Promise(r => setTimeout(r, 200));
      attempts++;
    }
    if (!window.Speedtest) {
      showToast('Speed test library failed to load. Please refresh.', 'error');
      setStep(STEP_FORM);
      return;
    }

    // Init LibreSpeed
    const st = new window.Speedtest();
    speedtestRef.current = st;

    st.setParameter('url_dl', '/api/speedtest/garbage');
    st.setParameter('url_ul', '/api/speedtest/empty');
    st.setParameter('url_ping', '/api/speedtest/empty');
    st.setParameter('url_getIp', '/api/speedtest/getIP');
    st.setParameter('time_dl_max', 15);
    st.setParameter('time_ul_max', 15);
    st.setParameter('count_ping', 10);
    st.setParameter('telemetry_level', 'none');

    st.onupdate = (data) => {
      const dl = parseFloat(data.dlStatus) || 0;
      const ul = parseFloat(data.ulStatus) || 0;
      const ping = parseFloat(data.pingStatus) || 0;
      const jitter = parseFloat(data.jitterStatus) || 0;
      const testState = parseInt(data.testState);

      setLiveValues({ download: dl, upload: ul, ping, jitter });

      // testState: 1=ping, 2=download, 3=upload, 4=finished, -1=abort
      if (testState === 1) setPhase(1);
      else if (testState === 2) setPhase(2);
      else if (testState === 3) setPhase(3);
      else if (testState === 4) setPhase(4);
    };

    st.onend = (aborted) => {
      if (aborted) {
        setStep(STEP_FORM);
        showToast('Speed test was cancelled', 'warning');
        return;
      }
      // Use last liveValues for final results
      setResults(prev => prev); // trigger re-read in effect
      setStep(STEP_RESULTS);
    };

    st.start();
  }

  // Capture final results when moving to STEP_RESULTS
  const finalResultsRef = useRef(null);
  useEffect(() => {
    if (step === STEP_RESULTS) {
      finalResultsRef.current = liveValues;
    }
  }, [step]); // eslint-disable-line

  async function submitResults() {
    const finalResults = finalResultsRef.current || liveValues;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('user', JSON.stringify({ ...form }));
      formData.append('speedtest', JSON.stringify({
        download: finalResults.download,
        upload:   finalResults.upload,
        ping:     finalResults.ping,
        jitter:   finalResults.jitter
      }));
      formData.append('client', JSON.stringify(clientInfo));
      images.forEach(img => formData.append('images', img));

      await api.post('/submit', formData);
      setStep(STEP_SUCCESS);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to submit results. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function handleImageChange(e) {
    const files = Array.from(e.target.files);
    const combined = [...images, ...files].slice(0, 5);
    setImages(combined);
    e.target.value = '';
  }

  function removeImage(idx) {
    setImages(prev => prev.filter((_, i) => i !== idx));
  }

  function resetAll() {
    setStep(STEP_FORM);
    setForm({ name: '', email: '', phone: '', description: '', features: '' });
    setErrors({});
    setResults(null);
    setClientInfo(null);
    setImages([]);
    setLiveValues({ download: 0, upload: 0, ping: 0, jitter: 0 });
    finalResultsRef.current = null;
    if (speedtestRef.current) { try { speedtestRef.current.abort(); } catch {} }
    speedtestRef.current = null;
  }

  // Load LibreSpeed main client script (speedtest.js) once
  useEffect(() => {
    if (window.Speedtest) return; // already loaded
    if (document.querySelector('script[data-librespeed-main]')) return;
    const s = document.createElement('script');
    s.src = '/speedtest.js';
    s.setAttribute('data-librespeed-main', '1');
    document.head.appendChild(s);
  }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
        </div>
      )}

      {/* Header */}
      <header style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-dim)',
        padding: '16px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/hdontap_logo.svg" alt="HDOnTap" style={{ height: 38 }} />
          <div style={{
            fontSize: 11, color: 'var(--accent-orange)', letterSpacing: '0.18em',
            textTransform: 'uppercase', fontWeight: 700, fontFamily: 'var(--font-display)'
          }}>Viewer Feedback</div>
        </div>
        <a href="/admin/login" style={{ color: '#b8c0cc', fontSize: 14, textDecoration: 'none', letterSpacing: '0.05em', fontWeight: 500 }}>
          Admin
        </a>
      </header>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 640 }}>

          {/* ── STEP: FORM ── */}
          {step === STEP_FORM && (
            <div style={{ animation: 'slideUp 0.3s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent-orange)', marginBottom: 10 }}>
                  Viewer Feedback &amp; System Diagnostic Test
                </h1>
                <p style={{ color: '#c8d0dc', fontSize: 15, lineHeight: 1.7 }}>
                  Please complete the form below, then run the system diagnostic test so our support team
                  can better identify and troubleshoot any issues you're experiencing with HDOnTap.com.
                  If possible, please include screenshots of the interface you're seeing, as they can be very helpful.
                </p>
              </div>

              <div className="card" style={{ padding: 28 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16, letterSpacing: '0.04em' }}>
                  * REQUIRED FIELDS
                </div>

                <div className="form-group">
                  <label className="label">Full Name *</label>
                  <input className={`input-field${errors.name ? ' error' : ''}`} name="name"
                    value={form.name} onChange={handleFormChange} placeholder="John Smith"
                    style={{ borderColor: errors.name ? 'var(--accent-red)' : undefined }} />
                  {errors.name && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 4 }}>{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="label">Email Address *</label>
                  <input className="input-field" name="email" type="email"
                    value={form.email} onChange={handleFormChange} placeholder="john@example.com"
                    style={{ borderColor: errors.email ? 'var(--accent-red)' : undefined }} />
                  {errors.email && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 4 }}>{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="label">Phone Number</label>
                  <input className="input-field" name="phone" type="tel"
                    value={form.phone} onChange={handleFormChange} placeholder="(555) 000-0000" />
                </div>

                <div className="form-group">
                  <label className="label">Please Describe the Issues You Are Having *</label>
                  <textarea className="input-field" name="description" rows={5}
                    value={form.description} onChange={handleFormChange}
                    placeholder="Please describe in as much detail as possible — what you see, when it happens, which browser/device you are using, etc."
                    style={{ borderColor: errors.description ? 'var(--accent-red)' : undefined }} />
                  {errors.description && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 4 }}>{errors.description}</div>}
                </div>

                <div className="form-group">
                  <label className="label">Please List Any Features That You Would Like to See Implemented</label>
                  <textarea className="input-field" name="features" rows={4}
                    value={form.features} onChange={handleFormChange}
                    placeholder="Any features or improvements you'd like to see on HDOnTap..." />
                </div>

                {/* Screenshot Upload */}
                <div className="form-group">
                  <label className="label">Screenshots <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 10, lineHeight: 1.6 }}>
                    If you're experiencing a visual issue, attach up to 5 screenshots to help the development team understand what you're seeing. PNG, JPG, and GIF are accepted.
                  </div>
                  {images.length < 5 && (
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                      border: '1px dashed var(--border-dim)', cursor: 'pointer',
                      color: 'var(--text-secondary)', fontSize: 13,
                      background: 'var(--bg-elevated)', transition: 'border-color 0.2s'
                    }}>
                      <span style={{ fontSize: 16 }}>+</span>
                      Add Screenshot{images.length > 0 ? ` (${images.length}/5)` : ''}
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                        onChange={handleImageChange} />
                    </label>
                  )}
                  {images.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {images.map((img, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                          borderRadius: 'var(--radius-sm)', padding: '5px 10px', fontSize: 12
                        }}>
                          <span style={{ color: 'var(--accent-orange)' }}>&#128247;</span>
                          <span style={{ color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {img.name}
                          </span>
                          <button onClick={() => removeImage(i)} style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', fontSize: 14, padding: '0 2px', lineHeight: 1
                          }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                  onClick={startTest}>
                  Run System Diagnostic Test
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: TEST RUNNING ── */}
          {step === STEP_TEST && (
            <div style={{ animation: 'slideUp 0.3s ease', textAlign: 'center' }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent-orange)', marginBottom: 8 }}>
                {PHASES[phase]}
              </h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 32, fontSize: 13 }}>
                Please wait while we test your connection...
              </p>

              {/* Main gauge */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
                <SpeedGauge
                  value={phase === 3 ? liveValues.upload : liveValues.download}
                  maxValue={500}
                  unit="Mbps"
                  label={phase === 3 ? 'Upload' : 'Download'}
                  active={true}
                />
              </div>

              {/* Mini stats */}
              <div className="grid-4" style={{ marginBottom: 24 }}>
                {[
                  { label: 'Download', value: liveValues.download.toFixed(1), unit: 'Mbps' },
                  { label: 'Upload',   value: liveValues.upload.toFixed(1),   unit: 'Mbps' },
                  { label: 'Ping',     value: liveValues.ping.toFixed(0),     unit: 'ms'   },
                  { label: 'Jitter',   value: liveValues.jitter.toFixed(1),   unit: 'ms'   },
                ].map(m => (
                  <div key={m.label} className="card" style={{ padding: '12px 8px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 600, color: 'var(--accent-orange)' }}>
                      {m.value}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {m.unit}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {m.label}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                {PHASES.slice(1, 4).map((p, i) => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: phase > i ? 'var(--accent-orange)' : 'var(--border-dim)',
                    transition: 'background 0.3s'
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* ── STEP: RESULTS ── */}
          {step === STEP_RESULTS && (
            <div style={{ animation: 'slideUp 0.3s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-orange)', marginBottom: 6 }}>
                  Test Complete
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  Review your results below, then submit to send them to our support team.
                </p>
              </div>

              {/* Speed metrics */}
              <div className="grid-4" style={{ marginBottom: 20 }}>
                {[
                  { label: 'Download', value: liveValues.download.toFixed(1), unit: 'Mbps', color: 'var(--accent-orange)' },
                  { label: 'Upload',   value: liveValues.upload.toFixed(1),   unit: 'Mbps', color: 'var(--accent-orange)' },
                  { label: 'Ping',     value: liveValues.ping.toFixed(0),     unit: 'ms',   color: 'var(--accent-cyan)' },
                  { label: 'Jitter',   value: liveValues.jitter.toFixed(1),   unit: 'ms',   color: 'var(--accent-cyan)' },
                ].map(m => (
                  <div key={m.label} className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 700, color: m.color }}>
                      {m.value}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {m.unit}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {m.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Client info */}
              {clientInfo && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-dim)' }}>
                    <div className="section-title">Connection & Device Info</div>
                  </div>
                  <div style={{ padding: '8px 0' }}>
                    {[
                      ['IP Address',     clientInfo.ip || '—'],
                      ['ISP',            clientInfo.isp || '—'],
                      ['Operating System', clientInfo.os],
                      ['Browser',        clientInfo.browser],
                      ['Viewport',       `${clientInfo.viewport.width} × ${clientInfo.viewport.height}px`],
                      ['Screen',         `${clientInfo.screenResolution.width} × ${clientInfo.screenResolution.height}px`],
                      ['Pixel Ratio',    `${clientInfo.devicePixelRatio}x`],
                      ['Language',       clientInfo.language],
                      ['Timezone',       clientInfo.timezone],
                    ].map(([label, value], i) => (
                      <div key={label} style={{
                        display: 'flex', padding: '8px 16px',
                        background: i % 2 === 1 ? 'rgba(26,31,43,0.5)' : 'transparent'
                      }}>
                        <div style={{ width: 160, color: 'var(--text-muted)', fontSize: 13, flexShrink: 0 }}>{label}</div>
                        <div style={{ color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{value}</div>
                      </div>
                    ))}
                    <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border-dim)', marginTop: 4 }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>User Agent</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontFamily: 'var(--font-mono)', wordBreak: 'break-all', lineHeight: 1.5 }}>{clientInfo.userAgent}</div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-ghost" onClick={resetAll} style={{ flex: '0 0 auto' }}>
                  Start Over
                </button>
                <button className="btn btn-primary" onClick={submitResults}
                  disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
                  {submitting ? (
                    <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Submitting...</>
                  ) : 'Submit & Send Results to Support'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: SUCCESS ── */}
          {step === STEP_SUCCESS && (
            <div style={{ animation: 'slideUp 0.3s ease', textAlign: 'center', padding: '40px 0' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'rgba(0,255,136,0.1)', border: '2px solid var(--accent-green)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px', fontSize: 32
              }}>✓</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-green)', marginBottom: 12 }}>
                Results Submitted!
              </h2>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 32, maxWidth: 420, margin: '0 auto 32px' }}>
                Your diagnostic test results have been sent to our support team.
                We'll review them and be in touch shortly.
              </p>
              <button className="btn btn-ghost" onClick={resetAll}>
                Run Another Test
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        textAlign: 'center', padding: '16px 24px',
        color: 'var(--text-muted)', fontSize: 11,
        borderTop: '1px solid var(--border-dim)',
        letterSpacing: '0.05em'
      }}>
        HDOnTap Viewer Feedback &mdash; Powered by LibreSpeed
      </footer>
    </div>
  );
}
